import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { fetchAwards } from "../lib/api";

export default function AwardsArchive() {
    const [archive, setArchive] = useState([]);
    const [active, setActive] = useState(null);

    useEffect(() => {
        fetchAwards()
            .then((data) => {
                const arch = data?.archive || [];
                setArchive(arch);
                if (arch.length) setActive(arch[0].period);
            })
            .catch(() => {});
    }, []);

    const activeGroup = archive.find((g) => g.period === active);

    return (
        <main data-testid="page-awards-archive" className="pt-32 md:pt-40">
            <Seo
                path="/awards/archive"
                title="Awards Archive 2011 – 2022"
                description="Recognition spanning over a decade of design excellence — historical awards received by ASC Interior Co., Ltd., 2011 – 2022."
            />

            <section className="px-6 md:px-12 lg:px-24 pb-12">
                <Link
                    to="/"
                    data-testid="archive-back"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]"
                >
                    <ArrowLeft size={14} /> Back to home
                </Link>
            </section>

            <section className="px-6 md:px-12 lg:px-24 pb-16">
                <Reveal>
                    <p className="asc-overline">Recognition · Archive</p>
                </Reveal>
                <Reveal delay={100}>
                    <h1 className="serif text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight font-light">
                        Awards Archive
                        <br />
                        <span className="italic">2011 — 2022.</span>
                    </h1>
                </Reveal>
                <Reveal delay={180}>
                    <p className="mt-10 max-w-2xl text-lg lg:text-xl text-[#5C5855] leading-relaxed">
                        Recognition spanning over a decade of design excellence.
                    </p>
                </Reveal>
            </section>

            <section className="px-6 md:px-12 lg:px-24 pb-32">
                <div data-testid="archive-tabs" className="flex flex-wrap gap-2 mb-10 border-y border-[#D1CAC2] py-4">
                    {archive.map((g) => (
                        <button
                            key={g.period}
                            onClick={() => setActive(g.period)}
                            data-testid={`archive-tab-${g.period.replace(/\s|–/g, "")}`}
                            className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] transition-colors ${
                                active === g.period
                                    ? "bg-[var(--asc-yellow)] text-[#1A1A1A]"
                                    : "text-[#5C5855] hover:text-[#1A1A1A]"
                            }`}
                        >
                            {g.period}
                        </button>
                    ))}
                </div>

                <div data-testid="archive-list" className="space-y-2">
                    {activeGroup?.items.map((a, i) => {
                        const Inner = (
                            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 py-5 border-b border-[#E5E1DB] group">
                                <span className="sm:col-span-8 serif text-lg lg:text-xl tracking-tight font-light group-hover:text-[var(--asc-accent-hover)] transition-colors">
                                    {a.title}
                                </span>
                                <span className="sm:col-span-3 sm:text-right text-sm text-[#5C5855] italic">
                                    {a.project}
                                </span>
                                <span className="sm:col-span-1 sm:text-right text-[var(--asc-yellow)] opacity-0 group-hover:opacity-100 transition-opacity">
                                    {a.project_slug ? "→" : ""}
                                </span>
                            </div>
                        );
                        return (
                            <Reveal key={i} delay={i * 50}>
                                {a.project_slug ? (
                                    <Link to={`/projects/${a.project_slug}`} className="block">
                                        {Inner}
                                    </Link>
                                ) : (
                                    <div>{Inner}</div>
                                )}
                            </Reveal>
                        );
                    })}
                </div>
            </section>
        </main>
    );
}
